<script lang="ts" setup>
    import { ref, computed, onMounted, watch } from "vue"
    import {
        useDataCacheStore,
        useAuthStore,
        useEntrepriseStore,
    } from "~/stores"
    import { useTheme } from "vuetify"
    import { clearIndexedDBCache } from "~/plugins/pinia-idb"

    const dataCacheStore = useDataCacheStore()
    const authStore = useAuthStore()
    const entrepriseStore = useEntrepriseStore()
    const theme = useTheme()

    const buildDate = __BUILD_DATE__

    const getBucketSize = computed(() => dataCacheStore.bucketSize)
    const me = computed(() => authStore.getUser)
    const entreprise = computed(() => entrepriseStore.currentItem)
    const canClearCache = computed(
        () => authStore.isAdmin || authStore.isPersonnel,
    )
    const canViewTelescope = computed(() => {
        if (authStore.isAdmin) return true
        const roles =
            me.value?.roles?.map((r: any) =>
                typeof r === "string" ? r : r?.name,
            ) || []
        return (
            roles.includes("commercial_director") ||
            roles.includes("technical_director")
        )
    })

    const darkMode = ref(false)
    const isClearing = ref(false)

    onMounted(() => {
        darkMode.value = localStorage.getItem("darkMode") === "true"
    })

    watch(darkMode, (value) => {
        localStorage.setItem("darkMode", value)
        theme.global.name.value = value ? "dark" : "light"
    })

    function logout() {
        authStore.logout()
    }

    async function clearCache() {
        if (isClearing.value) return

        isClearing.value = true
        try {
            const { deletedKeys } = await clearIndexedDBCache()
            alert(
                `Cache vidé : ${deletedKeys.length} entrées supprimées.\nL'application va se recharger.`,
            )
            window.location.reload()
        } catch (error) {
            console.error("Erreur lors du vidage du cache:", error)
            alert("Erreur lors du vidage du cache")
        } finally {
            isClearing.value = false
        }
    }
</script>

<template>
    <v-menu transition="scale-transition">
        <template v-slot:activator="{ props }">
            <AppAvatar
                :avatar_size="35"
                :personnel_id="me?.IDPERSONNEL"
                v-bind="props"
            />
        </template>
        <v-list item-props>
            <v-list-item>
                <v-list-item-title>{{ me?.NOM_COMPLET }}</v-list-item-title>
                <AppRoleBadge :roles="me?.roles" />
                <AppWebserviceBadge
                    :entreprise="entreprise"
                    v-if="entreprise"
                />
            </v-list-item>
            <v-divider />
            <v-list-item>
                <template v-slot:prepend>
                    <v-icon>mdi-database-outline</v-icon>
                </template>
                <v-list-item-title>
                    Mon espace : {{ getBucketSize }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item
                v-if="canClearCache"
                @click="clearCache()"
                :disabled="isClearing"
            >
                <template v-slot:prepend>
                    <v-icon>mdi-database-remove-outline</v-icon>
                </template>
                <v-list-item-title>
                    {{
                        isClearing
                            ? "Vidage en cours..."
                            : "Vider le cache local"
                    }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item
                v-if="canViewTelescope"
                :to="{ name: 'manager.telescopePage' }"
            >
                <template v-slot:prepend>
                    <v-icon>mdi-telescope</v-icon>
                </template>
                <v-list-item-title>Telescope</v-list-item-title>
            </v-list-item>
            <v-divider />
            <v-list-item @click="logout()">
                <template v-slot:prepend>
                    <v-icon>mdi-logout</v-icon>
                </template>
                <v-list-item-title>Déconnexion</v-list-item-title>
            </v-list-item>

            <v-divider />

            <v-list-item
                href="https://wikisolis.gitbook.io/lol-pp/"
                target="_blank"
            >
                <template v-slot:prepend>
                    <v-icon>mdi-help-circle</v-icon>
                </template>
                <v-list-item-title>Aide</v-list-item-title>
            </v-list-item>
            <v-list-item>
                <v-switch v-model="darkMode" label="Dark theme" />
            </v-list-item>
            <v-list-item>
                <p class="text-body-2">
                    MAJ: {{ new Date(buildDate).toLocaleString() }}
                </p>
            </v-list-item>
        </v-list>
    </v-menu>
</template>
